import React, { useState, useEffect, useRef, FormEvent } from "react";
import ReactDOM from "react-dom";
import type { CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import YogyismLogoSvg from "../tutorial/yogyismLogoSvg";
import { currentAccent } from "./accentBus";
import { db } from "../../services/firebase";
import { pauseSwiper, resumeSwiper } from "./swiperControl";
import { getSharedPlaying } from "./playingBus";
import ContactModal from "./Contactmodal";  // ← shared modal
import {
  loginWithEmail,
  resetPasswordEmail,
  changePassword,
} from "../../services/auth.service";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { useLoginModalText } from "./LoginModal.text";

// ─── Types ────────────────────────────────────────────────────────────────────
type ViewType = "login" | "forceReset" | "forgotPw" | "forgotSent";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  forceReset?: boolean;
}

// ─── Theme constants ──────────────────────────────────────────────────────────
const accent = "#378add";
const mono   = FONT_MONO;

// ─── Terms & Conditions consent helpers (localStorage, same as before) ───────



// Decorative starfield for the left panel
const LM_STARS: [number, number, number, number][] = [
  [12, 18, 1.1, 0.7], [38, 60, 0.7, 0.5], [70, 30, 1.0, 0.8], [88, 78, 0.8, 0.45],
  [22, 88, 0.9, 0.6], [60, 14, 0.7, 0.5], [92, 46, 1.0, 0.7], [46, 40, 0.6, 0.4],
  [78, 92, 0.9, 0.55], [16, 52, 0.8, 0.5],
];

const accentToRGB = (c: string): string => {
  if (!c || c === "white") return "255,255,255";
  const m = c.replace("#", "");
  if (m.length === 6) {
    const r = parseInt(m.slice(0, 2), 16);
    const g = parseInt(m.slice(2, 4), 16);
    const b = parseInt(m.slice(4, 6), 16);
    if (![r, g, b].some(isNaN)) return `${r},${g},${b}`;
  }
  return "55,138,221";
};

// ─── Eye icon ─────────────────────────────────────────────────────────────────
const EyeIcon: React.FC<{ off?: boolean; size?: number; color?: string }> = ({
  off = false, size = 18, color = "currentColor",
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size} height={size} viewBox="0 0 24 24"
    fill="none" stroke={color} strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ display: "block" }}
  >
    {off ? (
      <>
        <path d="M9.88 5.09A10.94 10.94 0 0 1 12 5c7 0 11 7 11 7a18.5 18.5 0 0 1-2.3 3.04" />
        <path d="M6.06 6.06C3.3 7.83 1 12 1 12s4 7 11 7a11 11 0 0 0 5.94-1.06" />
        <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
        <line x1="2" y1="2" x2="22" y2="22" />
      </>
    ) : (
      <>
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

// ─── Dream Orb ────────────────────────────────────────────────────────────────
// `width`/`height` are now props (defaulting to the original 280×176) instead
// of hard-coded constants, so the caller can render a smaller orb on mobile
// without the canvas's internal drawing math getting out of sync with its
// on-screen size (which is what causes visual squish/distortion).
const DreamOrb: React.FC<{ accent: string; width?: number; height?: number }> = ({
  accent, width = 280, height = 176,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; if (!ctx) return;

    const W = width, H = height;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr; canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const cx = W / 2, cy = H / 2 + 6;
    const C = accentToRGB(accent);

    let frame = 0, animId = 0;

    const orbs = [
      { r: 42, spd:  0.016, ph: 0,             sz: 2.8, tilt: 0.58 },
      { r: 54, spd: -0.011, ph: Math.PI * 0.6, sz: 2.2, tilt: 0.50 },
      { r: 66, spd:  0.009, ph: Math.PI * 1.2, sz: 3.4, tilt: 0.62 },
      { r: 78, spd: -0.007, ph: Math.PI * 1.8, sz: 2.0, tilt: 0.44 },
    ];
    const TRAIL = 22;
    const trails: { x: number; y: number }[][] = orbs.map(() => []);

    const dust = Array.from({ length: 90 }, (_, i) => {
      const arm = i % 3;
      const t   = i / 90;
      return {
        baseAngle: arm * ((Math.PI * 2) / 3) + t * Math.PI * 2.4,
        radius: 8 + t * 70,
        r: 0.4 + Math.random() * 1.1,
        tw: Math.random() * Math.PI * 2,
        sp: 0.004 + Math.random() * 0.006,
      };
    });

    const stars = Array.from({ length: 46 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      r: 0.3 + Math.random() * 1.2,
      tw: Math.random() * Math.PI * 2,
      spd: 0.010 + Math.random() * 0.022,
    }));

    const nebulae = [
      { x: cx - 60, y: cy - 32, r: 70, hue: C },
      { x: cx + 64, y: cy + 26, r: 64, hue: "150,90,230" },
      { x: cx + 20, y: cy - 40, r: 52, hue: "90,180,255" },
      { x: cx - 40, y: cy + 42, r: 58, hue: C },
    ];

    let shooting: { x: number; y: number; vx: number; vy: number; life: number } | null = null;
    let nextShoot = 90 + Math.random() * 120;

    function draw() {
      ctx.clearRect(0, 0, W, H);
      const pulse     = 0.5 + 0.5 * Math.sin(frame * 0.03);
      const slowPulse = 0.5 + 0.5 * Math.sin(frame * 0.012);
      const galRot    = frame * 0.004;

      nebulae.forEach((n, i) => {
        const drift = Math.sin(frame * 0.01 + i) * 4;
        const g = ctx.createRadialGradient(n.x + drift, n.y, 0, n.x + drift, n.y, n.r);
        g.addColorStop(0, `rgba(${n.hue},${(0.10 + 0.05 * slowPulse).toFixed(3)})`);
        g.addColorStop(1, `rgba(${n.hue},0)`);
        ctx.beginPath(); ctx.arc(n.x + drift, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = g; ctx.fill();
      });

      stars.forEach(s => {
        const tw = 0.3 + 0.7 * Math.abs(Math.sin(frame * s.spd + s.tw));
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${(tw * 0.5).toFixed(2)})`; ctx.fill();
      });

      dust.forEach(d => {
        const a = d.baseAngle + galRot;
        const x = cx + d.radius * Math.cos(a);
        const y = cy + d.radius * Math.sin(a) * 0.6;
        const tw = 0.3 + 0.7 * Math.abs(Math.sin(frame * d.sp + d.tw));
        ctx.beginPath(); ctx.arc(x, y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${C},${(tw * 0.42).toFixed(2)})`; ctx.fill();
      });

      for (let i = 0; i < 3; i++) {
        const rr = 36 + i * 18 + pulse * 5;
        ctx.beginPath(); ctx.ellipse(cx, cy, rr, rr * 0.6, 0, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${C},${(0.12 - i * 0.03).toFixed(3)})`;
        ctx.lineWidth = 1; ctx.stroke();
      }

      if (!shooting && frame >= nextShoot) {
        const startX = 20 + Math.random() * (W - 40);
        shooting = { x: startX, y: 10 + Math.random() * 20, vx: 2.4 + Math.random() * 1.6, vy: 1.0 + Math.random() * 0.8, life: 0 };
      }
      if (shooting) {
        shooting.x += shooting.vx; shooting.y += shooting.vy; shooting.life += 1;
        const tailX = shooting.x - shooting.vx * 9;
        const tailY = shooting.y - shooting.vy * 9;
        const grd = ctx.createLinearGradient(shooting.x, shooting.y, tailX, tailY);
        grd.addColorStop(0, "rgba(255,255,255,0.9)");
        grd.addColorStop(1, `rgba(${C},0)`);
        ctx.beginPath(); ctx.moveTo(shooting.x, shooting.y); ctx.lineTo(tailX, tailY);
        ctx.strokeStyle = grd; ctx.lineWidth = 1.6; ctx.lineCap = "round"; ctx.stroke();
        ctx.beginPath(); ctx.arc(shooting.x, shooting.y, 1.4, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,255,255,0.95)"; ctx.fill();
        if (shooting.x > W + 20 || shooting.y > H + 20 || shooting.life > 80) {
          shooting = null; nextShoot = frame + 120 + Math.random() * 160;
        }
      }

      const og = ctx.createRadialGradient(cx, cy, 0, cx, cy, 62);
      og.addColorStop(0, `rgba(${C},${(0.22 + 0.10 * pulse).toFixed(3)})`);
      og.addColorStop(0.5, `rgba(${C},${(0.06 + 0.03 * pulse).toFixed(3)})`);
      og.addColorStop(1, `rgba(${C},0)`);
      ctx.beginPath(); ctx.arc(cx, cy, 62, 0, Math.PI * 2); ctx.fillStyle = og; ctx.fill();

      orbs.forEach((orb, i) => {
        const a  = frame * orb.spd + orb.ph;
        const ox = cx + orb.r * Math.cos(a);
        const oy = cy + orb.r * Math.sin(a) * orb.tilt;

        trails[i].unshift({ x: ox, y: oy });
        if (trails[i].length > TRAIL) trails[i].pop();
        trails[i].forEach((tp, ti) => {
          const ta = (1 - ti / TRAIL) * 0.42;
          const tr = orb.sz * (1 - ti / TRAIL);
          if (tr > 0.25) {
            ctx.beginPath(); ctx.arc(tp.x, tp.y, tr, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(${C},${ta.toFixed(3)})`; ctx.fill();
          }
        });

        const pg = ctx.createRadialGradient(ox, oy, 0, ox, oy, orb.sz + 6);
        pg.addColorStop(0, "rgba(235,245,255,0.98)");
        pg.addColorStop(1, `rgba(${C},0)`);
        ctx.beginPath(); ctx.arc(ox, oy, orb.sz + 6, 0, Math.PI * 2); ctx.fillStyle = pg; ctx.fill();
        ctx.beginPath(); ctx.arc(ox, oy, orb.sz, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(248,252,255,1)"; ctx.fill();
      });

      ctx.save(); ctx.translate(cx, cy); ctx.rotate(galRot * 2);
      for (let i = 0; i < 8; i++) {
        const ang = (i / 8) * Math.PI * 2;
        const len = i % 2 === 0 ? 20 : 12;
        ctx.beginPath(); ctx.moveTo(0, 0);
        ctx.lineTo(Math.cos(ang) * len, Math.sin(ang) * len * 0.7);
        ctx.strokeStyle = `rgba(${C},${(0.16 + 0.10 * pulse).toFixed(3)})`;
        ctx.lineWidth = 1; ctx.lineCap = "round"; ctx.stroke();
      }
      ctx.restore();

      const cg = ctx.createRadialGradient(cx, cy, 0, cx, cy, 16);
      cg.addColorStop(0,    "rgba(255,255,255,1)");
      cg.addColorStop(0.35, `rgba(${C},0.95)`);
      cg.addColorStop(1,    `rgba(${C},0)`);
      ctx.beginPath(); ctx.arc(cx, cy, 16, 0, Math.PI * 2); ctx.fillStyle = cg; ctx.fill();
      ctx.beginPath(); ctx.arc(cx, cy, 3 + pulse * 1.6, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff"; ctx.fill();

      frame++; animId = requestAnimationFrame(draw);
    }

    draw();
    return () => cancelAnimationFrame(animId);
  }, [accent, width, height]);

  return (
    <canvas
      ref={canvasRef}
      // height:"auto" + aspectRatio (instead of a hard-coded pixel height)
      // keeps the orb's proportions correct if maxWidth ever clamps the
      // display width down on very narrow screens.
      style={{ display: "block", width, height: "auto", aspectRatio: `${width} / ${height}`, maxWidth: "100%" }}
    />
  );
};

// ─── Terms & Conditions modal (read-only, opened from the checkbox link) ─────
interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  dynamicAccent: string;
}

const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose, dynamicAccent }) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const tt = useLoginModalText().terms;

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
    } else {
      const t = setTimeout(() => setMounted(false), 220);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    if (isOpen) window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [isOpen, onClose]);

  if (!mounted) return null;

  const sections = tt.sections;

  const content = (
    <div
      ref={overlayRef}
      style={{
        position: "fixed", inset: 0, zIndex: 10050,
        background: "rgba(3,7,18,0.88)", backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, fontFamily: mono,
        animation: isOpen ? "tm-fadeIn 0.2s ease forwards" : "tm-fadeOut 0.2s ease forwards",
      }}
      onClick={e => { if (e.target === overlayRef.current) onClose(); }}
    >
      <style>{`
        @keyframes tm-fadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes tm-fadeOut { from{opacity:1} to{opacity:0} }
      `}</style>
      <div style={{
        background: "#0b1628", border: `1px solid ${dynamicAccent}4d`, borderRadius: 18,
        width: "min(460px, 94vw)", maxHeight: "84vh", overflowY: "auto",
        padding: "22px 22px 20px", position: "relative",
        boxShadow: `0 20px 60px rgba(0,0,0,0.6)`,
      }}>
        <button
          onClick={onClose}
          aria-label={tt.closeAria}
          style={{
            position: "absolute", top: 14, right: 14,
            width: 28, height: 28, borderRadius: "50%",
            background: "rgba(8,22,38,0.6)", border: "1px solid rgba(120,180,230,0.18)",
            color: "rgba(150,200,240,0.75)", fontSize: 15, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >✕</button>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 24 }}>📜</span>
          <h2 style={{ fontSize: 17, fontWeight: 800, color: "#e2e8f0", margin: 0 }}>
            {tt.title}
          </h2>
        </div>

        <p style={{ fontSize: 13, color: "#a8bdd1", lineHeight: 1.55, margin: "8px 0 14px" }}>
          {tt.intro}
        </p>

        {sections.map(({ title, body }) => (
          <div key={title} style={{ marginBottom: 14 }}>
            <div style={{
              fontSize: 10.5, fontWeight: 800, color: dynamicAccent,
              letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5,
            }}>{title}</div>
            <div style={{ fontSize: 12, color: "#a8bdd1", lineHeight: 1.55 }}>{body}</div>
          </div>
        ))}

        <button
          onClick={onClose}
          style={{
            width: "100%", marginTop: 6,
            background: dynamicAccent, color: "#031018",
            border: "none", borderRadius: 10, padding: "11px 18px",
            fontSize: 13, fontWeight: 800, cursor: "pointer",
          }}
        >
          {tt.gotIt}
        </button>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

// ─── LoginModal ───────────────────────────────────────────────────────────────
const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, forceReset = false }) => {
  const [view, setView]                       = useState<ViewType>(forceReset ? "forceReset" : "login");
  const [email, setEmail]                     = useState("");
  const [password, setPassword]               = useState("");
  const [showPw, setShowPw]                   = useState(false);
  const [resetEmail, setResetEmail]           = useState("");
  const [newPassword, setNewPassword]         = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loggedInUser, setLoggedInUser]       = useState<any>(null);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState("");
  const [mounted, setMounted]                 = useState(false);
  const [showContact, setShowContact]         = useState(false);
  const [agreedTerms, setAgreedTerms]         = useState(false); // ← Terms & Conditions checkbox
  const [showTerms, setShowTerms]             = useState(false); // ← Terms & Conditions modal
  const navigate                              = useNavigate();
  const [dynamicAccent, setDynamicAccent]     = useState("white");
  const overlayRef                            = useRef<HTMLDivElement>(null);
  const t                                     = useLoginModalText();

  // ── Mobile breakpoint — same 640px pattern used in SlideToolbar. Below
  //    this width the two-panel side-by-side card gets swapped for a
  //    stacked, scrollable layout instead of squeezing both panels into a
  //    sliver of the screen (which is what was making the logo/branding
  //    panel look broken).
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 640
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const wasPlayingRef = useRef(true);
  const didOpenRef    = useRef(false);

  useEffect(() => {
    if (isOpen) {
      didOpenRef.current = true;
      wasPlayingRef.current = getSharedPlaying();
      window.dispatchEvent(new Event("yogyism:stopAudio"));
      pauseSwiper();
    } else {
      if (!didOpenRef.current) return;
      didOpenRef.current = false;
      if (wasPlayingRef.current) resumeSwiper();
      window.dispatchEvent(new Event("yogyism:modalClosed"));
    }
  }, [isOpen]);

  useEffect(() => {
    const handler = (e: Event) => {
      const a = (e as CustomEvent).detail?.accent;
      if (a) setDynamicAccent(a);
    };
    window.addEventListener("yogyism:slideAccent", handler);
    return () => window.removeEventListener("yogyism:slideAccent", handler);
  }, []);

useEffect(() => {
  if (isOpen) {
    setMounted(true);
    setView(forceReset ? "forceReset" : "login");
    setEmail(""); setPassword(""); setResetEmail("");
    setNewPassword(""); setConfirmPassword("");
    setError(""); setLoading(false); setShowPw(false);
    setAgreedTerms(false); // always require a fresh, active click on every open
    setShowTerms(false);
  } else {
    const t = setTimeout(() => setMounted(false), 280);
    return () => clearTimeout(t);
  }
}, [isOpen, forceReset]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    if (isOpen) window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [isOpen, onClose]);

  if (!mounted) return null;

  const handleAuthError = (err: any) => {
    setError(t.errors.codes[err?.code] || t.errors.signInFailed);
  };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    if (!agreedTerms) { setError(t.errors.agreeTerms); return; }
    if (!email || !password) { setError(t.errors.enterEmailPw); return; }
    setLoading(true); setError("");
    try {
      const cred: any = await loginWithEmail(email, password);
      const user = cred.user;
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists() && snap.data().forcePasswordReset === true) {
        setLoggedInUser(user);
        setView("forceReset");
      } else {
        onClose();
        navigate("/session");
      }
    } catch (err) {
      handleAuthError(err);
    } finally { setLoading(false); }
  };

  const handleForceReset = async (e: FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) { setError(t.errors.pwTooShort); return; }
    if (newPassword !== confirmPassword) { setError(t.errors.pwMismatch); return; }
    setLoading(true); setError("");
    try {
      await changePassword(newPassword);
      await updateDoc(doc(db, "users", loggedInUser.uid), { forcePasswordReset: false });
      onClose();
      navigate("/session");
    } catch {
      setError(t.errors.updateFailed);
    } finally { setLoading(false); }
  };

  const handleForgotPw = async (e: FormEvent) => {
    e.preventDefault();
    if (!resetEmail) { setError(t.errors.enterEmail); return; }
    setLoading(true); setError("");
    try {
      await resetPasswordEmail(resetEmail);
      setView("forgotSent");
    } catch (err: any) {
      setError(err?.code === "auth/user-not-found"
        ? t.errors.noAccount
        : t.errors.resetFailed);
    } finally { setLoading(false); }
  };

  // ── Styles ──────────────────────────────────────────────────────────────────
  const overlay: CSSProperties = {
    position: "fixed", inset: 0, zIndex: 9999,
    background: "radial-gradient(circle at 50% 40%, rgba(6,16,30,0.62), rgba(2,8,18,0.82))",
    backdropFilter: "blur(12px) saturate(0.55)",
    WebkitBackdropFilter: "blur(12px) saturate(0.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: isMobile ? 12 : 20,
    animation: isOpen ? "lm-fadeIn 0.28s ease forwards" : "lm-fadeOut 0.28s ease forwards",
  };
  // Card: side-by-side row on desktop (unchanged), stacked column on mobile
  // with an auto/scrollable height instead of a fixed 560px — that's what
  // was crushing both panels into an unreadable sliver before.
  const card: CSSProperties = {
    background: "#04101d",
    border: `1px solid ${dynamicAccent}4d`,
    borderRadius: 18,
    boxShadow: `0 0 0 1px ${dynamicAccent}1a, 0 0 70px ${dynamicAccent}33, 0 26px 70px rgba(0,0,0,0.72)`,
    width: isMobile ? "94vw" : "min(820px, 92vw)",
    height: isMobile ? "auto" : "min(560px, 92vh)",
    maxHeight: "92vh",
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    overflowY: isMobile ? "auto" : "hidden",
    overflowX: "hidden",
    position: "relative",
    animation: isOpen
      ? "lm-slideUp 0.30s cubic-bezier(0.34,1.56,0.64,1) forwards"
      : "lm-slideDown 0.28s ease forwards",
  };
  const inputStyle: CSSProperties = {
    width: "100%", boxSizing: "border-box",
    background: "rgba(5,18,32,0.80)",
    border: "1px solid rgba(55,138,221,0.22)",
    borderLeft: `2px solid ${dynamicAccent}66`,
    borderRadius: 8, padding: "13px 16px",
    color: "#e2f0fc", fontFamily: FONT_BODY, fontSize: 15, fontStyle: "normal",
    outline: "none", transition: "border-color 0.2s, box-shadow 0.2s, background 0.2s",
    marginBottom: 13,
  };
  const btnPrimary: CSSProperties = {
    width: "100%", padding: "14px 0",
    background: `linear-gradient(135deg, ${dynamicAccent} 0%, #1a5fa8 100%)`,
    border: "none", borderRadius: 8,
    color: "#fff", fontFamily: mono, fontSize: 14,
    letterSpacing: 2.5, textTransform: "uppercase",
    cursor: (loading || !agreedTerms) ? "not-allowed" : "pointer",
    opacity: (loading || !agreedTerms) ? 0.5 : 1,
    boxShadow: `0 8px 24px ${dynamicAccent}3a`,
    transition: "opacity 0.2s, transform 0.15s, box-shadow 0.2s", marginTop: 6,
  };
  const lbl: CSSProperties = {
    fontSize: 11, fontFamily: mono, color: dynamicAccent,
    letterSpacing: 3, textTransform: "uppercase",
    marginBottom: 12, display: "inline-flex", alignItems: "center", gap: 8,
  };
  const headline: CSSProperties = {
    fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600,
    fontSize: isMobile ? 22 : 28, lineHeight: 1.2, color: "#eaf4ff",
    margin: "0 0 24px",
  };
  const linkBtn: CSSProperties = {
    background: "none", border: "none", color: dynamicAccent,
    fontFamily: FONT_BODY, fontSize: 14, fontStyle: "normal",
    cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, padding: 0,
  };
  const closeBtn: CSSProperties = {
    position: "absolute", top: 14, right: 16, zIndex: 3,
    width: 32, height: 32, borderRadius: "50%",
    background: "rgba(8,22,38,0.6)", border: "1px solid rgba(120,180,230,0.18)",
    color: "rgba(150,200,240,0.65)", fontSize: 17,
    cursor: "pointer", lineHeight: 1,
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "color 0.2s, border-color 0.2s, background 0.2s",
  };
  const errorPill: CSSProperties = {
    display: "flex", alignItems: "center", gap: 8,
    background: "rgba(224,112,112,0.10)",
    border: "1px solid rgba(224,112,112,0.32)",
    borderRadius: 7, padding: "9px 13px",
    color: "#f0a8a8", fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 14,
    margin: "2px 0 13px",
  };
  const eyeBtn: CSSProperties = {
    position: "absolute", right: 10, top: "calc(50% - 6px)", transform: "translateY(-50%)",
    background: "none", border: "none", color: "#7aa8cc", cursor: "pointer",
    padding: 4, lineHeight: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "color 0.2s",
  };
  const contactBtn: CSSProperties = {
    background: `${dynamicAccent}1f`,
    border: `1px solid ${dynamicAccent}`,
    padding: "8px 20px", borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    fontSize: 12,
    fontFamily: mono,
    letterSpacing: 3,
    textTransform: "uppercase",
    fontStyle: "normal",
    transition: "border-color 0.2s, color 0.2s, background 0.2s",
    opacity: 1,
  };
  const termsRow: CSSProperties = {
    display: "flex", alignItems: "flex-start", gap: 9,
    margin: "2px 0 14px", cursor: "pointer",
  };
  const termsCheckbox: CSSProperties = {
    marginTop: 2, width: 16, height: 16, accentColor: dynamicAccent,
    cursor: "pointer", flexShrink: 0,
  };
  const termsText: CSSProperties = {
    fontSize: 12.5, color: "#a8bdd1", fontFamily: FONT_BODY, fontStyle: "normal",
    lineHeight: 1.4,
  };
  const termsLinkBtn: CSSProperties = {
    background: "none", border: "none", padding: 0,
    color: dynamicAccent, fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 12.5,
    textDecoration: "underline", textUnderlineOffset: 2, cursor: "pointer",
  };

  const ErrorPill = ({ msg }: { msg: string }) => (
    <div style={errorPill}>
      <span style={{ flexShrink: 0 }}>⚠</span>
      <span>{msg}</span>
    </div>
  );

  const modalContent = (
    <>
      <style>{`
        @keyframes lm-fadeIn    { from{opacity:0} to{opacity:1} }
        @keyframes lm-fadeOut   { from{opacity:1} to{opacity:0} }
        @keyframes lm-slideUp   { from{opacity:0;transform:translateY(28px) scale(0.96)} to{opacity:1;transform:none} }
        @keyframes lm-slideDown { from{opacity:1;transform:none} to{opacity:0;transform:translateY(18px) scale(0.97)} }
        @keyframes lm-twinkle   { 0%,100%{opacity:0.25} 50%{opacity:0.9} }
        @keyframes lm-nodePulse { 0%,100%{transform:translate(-50%,-50%) scale(1);opacity:0.85} 50%{transform:translate(-50%,-50%) scale(1.5);opacity:0.35} }
        .lm-input:focus         { border-color: rgba(55,138,221,0.70) !important; box-shadow: 0 0 0 3px rgba(55,138,221,0.12) !important; background: rgba(7,22,38,0.92) !important; }
        .lm-btn-primary:hover:not(:disabled) { transform: translateY(-1px); opacity: 0.95; }
        .lm-close:hover         { color: rgba(190,225,255,0.95) !important; border-color: rgba(150,200,240,0.45) !important; background: rgba(14,30,48,0.85) !important; }
        .lm-contact:hover       { background: ${dynamicAccent}33 !important; box-shadow: 0 0 14px ${dynamicAccent}55 !important; }
        .lm-eye:hover           { color: rgba(180,220,255,0.95) !important; }
        .lm-star                { animation: lm-twinkle 3.4s ease-in-out infinite; }
      `}</style>

      <div
        ref={overlayRef}
        style={overlay}
        onClick={e => { if (e.target === overlayRef.current) onClose(); }}
      >
        <div style={card}>
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 1,
            background: `linear-gradient(90deg, transparent, ${dynamicAccent}80, transparent)`,
            pointerEvents: "none", zIndex: 2,
          }} />

          <button className="lm-close" style={closeBtn} onClick={onClose}>✕</button>

          {/* LEFT PANEL — full width & stacked on top when mobile, instead of
              being squeezed into half the row alongside the form panel. */}
          <div style={{
            flex: isMobile ? "0 0 auto" : 1, position: "relative",
            background: `linear-gradient(155deg, ${dynamicAccent}33 0%, ${dynamicAccent}14 42%, #071a2e 100%)`,
            display: "flex", flexDirection: "column",
            justifyContent: "flex-start", alignItems: "center",
            padding: isMobile ? "34px 20px 18px" : "46px 30px 22px",
            overflow: "hidden",
          }}>
            <div style={{
              position: "absolute", top: "22%", left: "50%",
              width: 250, height: 250, transform: "translate(-50%,-50%)",
              background: `radial-gradient(circle, ${dynamicAccent}40 0%, transparent 70%)`,
              pointerEvents: "none",
            }} />
            <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
              {LM_STARS.map(([x, y, r, o], i) => (
                <span key={i} className="lm-star" style={{
                  position: "absolute", left: `${x}%`, top: `${y}%`,
                  width: r * 2, height: r * 2, borderRadius: "50%",
                  background: "#ffffff", opacity: o,
                  animationDelay: `${(i % 5) * 0.6}s`,
                }} />
              ))}
            </div>

            <h1 style={{
              position: "relative",
              fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600,
              fontSize: isMobile ? 24 : 32, letterSpacing: 0.5, color: "#eaf4ff",
              margin: isMobile ? "0 0 16px" : "0 0 22px", textAlign: "center", lineHeight: 1.1,
            }}>
              Dreamfulness
            </h1>

            <div style={{
              position: "relative",
              width: "clamp(48px,6vw,72px)", height: "clamp(48px,6vw,72px)",
              border: `1px solid ${dynamicAccent}66`,
              background: `${dynamicAccent}1f`,
              borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              overflow: "hidden", flexShrink: 0,
              boxShadow: `0 0 28px ${dynamicAccent}40, inset 0 0 18px ${dynamicAccent}22`,
            }}>
              <YogyismLogoSvg width="72%" height="72%" color="white" />
            </div>

            <p style={{
              fontFamily: mono, fontSize: isMobile ? 14 : 16, fontWeight: 700,
              letterSpacing: 3, textTransform: "uppercase",
              color: "#ffffff", opacity: 1,
              margin: "14px 0 0", textAlign: "center",
            }}>
              {t.brandBy}{" "}
              <a href="https://www.yogyism.org/" target="_blank" rel="noopener noreferrer" style={{ color: "#ffffff", textDecoration: "underline", textUnderlineOffset: 3 }}>Yogyism↗</a>
            </p>

            <div style={{
              width: 48, height: 1, marginTop: 18,
              background: `linear-gradient(90deg, transparent, ${dynamicAccent}99, transparent)`,
            }} />

            {/* ── Contact Us → opens ContactModal instead of Gmail ── */}
            <div style={{ marginTop: 22 }}>
              <button
                className="lm-contact"
                style={contactBtn}
                onClick={() => setShowContact(true)}
              >
                {t.contactUs}
              </button>
            </div>

            {/* Orb is smaller on mobile and no longer pinned to the bottom
                via marginTop:auto — with the card's height now "auto" on
                mobile there's no leftover space to push into, so pinning it
                would have no visible effect; it just flows after the button
                with a fixed top gap instead. */}
            <div style={{
              position: "relative",
              marginTop: isMobile ? 18 : "auto",
              width: "100%", display: "flex", justifyContent: "center",
              paddingTop: isMobile ? 0 : 8,
            }}>
              <DreamOrb accent={dynamicAccent} width={isMobile ? 200 : 280} height={isMobile ? 126 : 176} />
            </div>
          </div>

          {/* Separator — vertical divider on desktop, horizontal on mobile
              (since the panels stack top/bottom instead of side by side). */}
          <div style={{
            position: "relative",
            width: isMobile ? "100%" : 1,
            height: isMobile ? 1 : "auto",
            flexShrink: 0,
            alignSelf: isMobile ? "stretch" : "stretch",
            margin: isMobile ? "0" : "30px 0",
          }}>
            <div style={isMobile ? {
              position: "absolute", left: 0, right: 0, top: "50%", height: 12,
              transform: "translateY(-50%)",
              background: `linear-gradient(90deg, transparent, ${dynamicAccent}66, transparent)`,
              filter: "blur(6px)", pointerEvents: "none",
            } : {
              position: "absolute", top: 0, bottom: 0, left: "50%", width: 12,
              transform: "translateX(-50%)",
              background: `linear-gradient(180deg, transparent, ${dynamicAccent}66, transparent)`,
              filter: "blur(6px)", pointerEvents: "none",
            }} />
            <div style={{
              position: "absolute", inset: 0,
              background: isMobile
                ? `linear-gradient(90deg, transparent, ${dynamicAccent}dd 50%, transparent)`
                : `linear-gradient(180deg, transparent, ${dynamicAccent}dd 50%, transparent)`,
            }} />
            <div style={{
              position: "absolute", top: "50%", left: "50%",
              width: 5, height: 5, borderRadius: "50%",
              background: dynamicAccent,
              boxShadow: `0 0 10px ${dynamicAccent}`,
              animation: "lm-nodePulse 3s ease-in-out infinite",
            }} />
          </div>

          {/* RIGHT PANEL */}
          <div style={{
            flex: isMobile ? "0 0 auto" : 1.15, display: "flex", flexDirection: "column",
            justifyContent: "center",
            padding: isMobile ? "24px 22px 30px" : "44px 46px",
            background: "linear-gradient(180deg, #04101d 0%, #030b16 100%)",
          }}>
            {view === "login" && (
              <>
                <span style={lbl}>
                  <span style={{ width: 16, height: 1, background: dynamicAccent, display: "inline-block" }} />
                  {t.login.label}
                </span>
                <h2 style={headline}>{t.login.headline}</h2>
                <form onSubmit={handleLogin}>
                  <input className="lm-input" style={inputStyle} type="email"
                    value={email} onChange={e => setEmail(e.target.value)} placeholder={t.login.email} />
                  <div style={{ position: "relative" }}>
                    <input className="lm-input"
                      style={{ ...inputStyle, paddingRight: 44 }}
                      type={showPw ? "text" : "password"}
                      value={password} onChange={e => setPassword(e.target.value)} placeholder={t.login.password} />
                    <button type="button" className="lm-eye" onClick={() => setShowPw(v => !v)}
                      style={eyeBtn} aria-label={showPw ? t.login.hidePw : t.login.showPw}>
                      <EyeIcon off={showPw} size={18} />
                    </button>
                  </div>

                  {/* ── Terms & Conditions checkbox + link ── */}
                  <label style={termsRow}>
                    <input
                      type="checkbox"
                      checked={agreedTerms}
                      onChange={e => setAgreedTerms(e.target.checked)}
                      style={termsCheckbox}
                    />
                    <span style={termsText}>
                      {t.login.agreePre}{" "}
                      <button
                        type="button"
                        onClick={() => setShowTerms(true)}
                        style={termsLinkBtn}
                      >
                        {t.login.termsLink}
                      </button>
                    </span>
                  </label>

                  {error && <ErrorPill msg={error} />}
                  <button className="lm-btn-primary" style={btnPrimary} disabled={loading || !agreedTerms}>
                    {loading ? t.login.loading : t.login.submit}
                  </button>
                </form>
                <div style={{ marginTop: 18 }}>
                  <button style={linkBtn} onClick={() => setView("forgotPw")}>{t.login.forgot}</button>
                </div>
              </>
            )}

            {view === "forceReset" && (
              <>
                <span style={lbl}>
                  <span style={{ width: 16, height: 1, background: dynamicAccent, display: "inline-block" }} />
                  {t.forceReset.label}
                </span>
                <h2 style={headline}>{t.forceReset.headline}</h2>
                <form onSubmit={handleForceReset}>
                  <input className="lm-input" style={inputStyle} type="password"
                    value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder={t.forceReset.newPw} />
                  <input className="lm-input" style={inputStyle} type="password"
                    value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder={t.forceReset.confirmPw} />
                  {error && <ErrorPill msg={error} />}
                  <button className="lm-btn-primary" style={btnPrimary} disabled={loading}>
                    {loading ? t.forceReset.loading : t.forceReset.submit}
                  </button>
                </form>
              </>
            )}

            {view === "forgotPw" && (
              <>
                <span style={lbl}>
                  <span style={{ width: 16, height: 1, background: dynamicAccent, display: "inline-block" }} />
                  {t.forgot.label}
                </span>
                <h2 style={headline}>{t.forgot.headline}</h2>
                <form onSubmit={handleForgotPw}>
                  <input className="lm-input" style={inputStyle} type="email"
                    value={resetEmail} onChange={e => setResetEmail(e.target.value)} placeholder={t.forgot.email} />
                  {error && <ErrorPill msg={error} />}
                  <button className="lm-btn-primary" style={btnPrimary} disabled={loading}>
                    {loading ? t.forgot.loading : t.forgot.submit}
                  </button>
                </form>
                <button style={{ ...linkBtn, marginTop: 18 }} onClick={() => setView("login")}>
                  {t.forgot.back}
                </button>
              </>
            )}

            {view === "forgotSent" && (
              <div style={{ textAlign: "center" }}>
                <div style={{
                  fontFamily: FONT_DISPLAY, fontWeight: 600, width: 56, height: 56, margin: "0 auto 20px",
                  borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 26,
                  background: `${dynamicAccent}1f`, border: `1px solid ${dynamicAccent}59`,
                  boxShadow: `0 0 24px ${dynamicAccent}33`,
                }}>📩</div>
                <h2 style={{ ...headline, marginBottom: 10 }}>{t.sent.headline}</h2>
                <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 15, color: "#7aa8cc", margin: 0 }}>
                  {t.sent.check} <span style={{ color: dynamicAccent }}>{resetEmail}</span>
                </p>
                <button className="lm-btn-primary" style={{ ...btnPrimary, marginTop: 26 }} onClick={() => setView("login")}>
                  {t.sent.back}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contact modal rendered via portal at body level, above LoginModal */}
      <ContactModal
        isOpen={showContact}
        onClose={() => setShowContact(false)}
        accent={dynamicAccent}
        accentRgb={accentToRGB(dynamicAccent)}
      />

      {/* Terms & Conditions modal — opened by clicking the link in the checkbox row */}
      <TermsModal
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
        dynamicAccent={dynamicAccent}
      />
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default LoginModal;